import { useLocation } from "react-router-dom";

import { categoryFromSearch } from "../modules/home/homeTiles";
import {
  BookingWindowSettings,
  EntrySequence,
  HeadCountLabel,
  TenantConfigService,
  TenantConfig,
  UseCaseFlowConfig,
} from "../types/tenantConfig";
import { STANDARD_CATEGORY_KEY } from "../modules/home/homeTiles";
import { CANCELLATION_NONE, CANCELLATION_UNREADABLE, CancellationRule } from "./cancellationRule";

/**
 * Reading tenant configuration when the unit of configuration is the PRODUCT
 * CATEGORY (ADR-0026 §B).
 *
 * THE RULE, STATED ONCE: **category first, use case as the fallback.** A
 * tenant that has configured nothing per category keeps exactly the behaviour
 * it has today, which is what lets the category layer deploy with no migration
 * and no flag day. A tenant that configures one knob on one category keeps its
 * use-case values for every other knob and every other category.
 *
 * The fallback is not a transitional kindness — it is the contract. It is
 * removed only once every operational tenant's config is migrated, and that
 * removal is its own PR (plan §4.1).
 *
 * WHY THE READERS ARE NAMED RATHER THAN GENERIC. Five fields resolve the same
 * way, and a single stringly-typed generic would have to give up the return
 * type to do it. Each reader below is one expression, and
 * `__tests__/categoryConfig.test.ts` drives ALL FIVE through the same three
 * cases, so a reader that forgot to fall back cannot pass.
 */

type CategoryConfig = NonNullable<
  NonNullable<TenantConfig["categories"]>[number]["config"]
>;

/** The use-case key as the config blocks store it. */
const key = (useCase: TenantConfigService | string): TenantConfigService =>
  useCase.toLowerCase() as TenantConfigService;

/**
 * The configuration block of the category currently being booked, if any.
 *
 * One lookup, so "which category is this" has a single answer. An unknown key
 * — a stale link, a category the tenant deleted — finds nothing and every
 * reader below falls through to the use case, which is the same thing the
 * customer would have seen before the category existed.
 *
 * The category must belong to THIS use case. Keys are unique per tenant, not
 * per use case, so an apartment category's key can arrive on the venue flow —
 * and since #2764 F9 the search IGNORES such a key and lists every venue. Had
 * this matched on the key alone, those venues would then be booked under the
 * apartment category's settings: its direct-booking mode, booking window,
 * event types and check-in flow. The use case is compared exactly as the
 * fallback below indexes it, so both sides agree on what "this use case" is.
 */
export const categoryConfigFor = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey: string | undefined
): CategoryConfig | undefined => {
  if (!categoryKey) return undefined;
  // The `_standard` tile IS the standard product category (ADR-0026 §AE/§AI):
  // until the one-off fill it also lists the uncategorised products, and its
  // settings live on the real row `standard_<useCase>`.
  const lookup = categoryKey === STANDARD_CATEGORY_KEY
    ? `standard_${key(useCase)}`
    : categoryKey;
  return config?.categories?.find(
    (c) =>
      c.key === lookup &&
      typeof c.useCase === "string" &&
      key(c.useCase) === key(useCase)
  )?.config;
};

export const resolveCharging = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): "free" | "mixed" | "chargeable" | undefined =>
  categoryConfigFor(config, useCase, categoryKey)?.charging ??
  config?.charging?.[key(useCase)];

export const resolveBookingWindowSettings = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): BookingWindowSettings | undefined =>
  categoryConfigFor(config, useCase, categoryKey)?.bookingWindow ??
  config?.bookingWindow?.[key(useCase)];

export const resolveDirectBooking = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): boolean | undefined =>
  categoryConfigFor(config, useCase, categoryKey)?.directBooking ??
  config?.directBooking?.[key(useCase)];

export const resolveEventTypes = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): string[] | undefined =>
  categoryConfigFor(config, useCase, categoryKey)?.eventTypes ??
  config?.eventTypes?.[key(useCase)];

/**
 * The cancellation rule a booking of this category runs under (M10), or
 * `undefined` = no rule, the AGB governs. Category first, use case (the
 * standard category) as the fallback, like the booking fields above — with
 * one extra state: a category's `"none"` stops the fallback, so a category can
 * say "no rule here" under a use case that has one. The server answers the
 * same question in `resolveCancellation` (new-backend/app
 * `lib/booking/cancellationRule.ts`); a change to either changes both.
 */
export const resolveCancellationRule = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): CancellationRule | undefined => {
  const own = categoryConfigFor(config, useCase, categoryKey)?.cancellation;
  // "none" and "unreadable" both stop the fallback: the AGB sentence, never
  // the use case's rule.
  if (own === CANCELLATION_NONE || own === CANCELLATION_UNREADABLE) return undefined;
  return own ?? config?.cancellation?.[key(useCase)];
};

/**
 * The note this category shows on the venue booking step, or `undefined`
 * (#2889).
 *
 * The one reader here WITHOUT a use-case fallback, and on purpose: no use case
 * carries a note, and an absent note renders nothing — which replaced a
 * sentence that was hardcoded for one tenant and shown to all of them.
 */
export const resolveCategoryNote = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): { de?: string; en?: string } | undefined =>
  categoryConfigFor(config, useCase, categoryKey)?.note;

/**
 * Does this category's booking flow open on the OBJECT? (#3035, M1)
 *
 * THE switch-on check of the object-first entry: every screen that behaves
 * differently for it asks this, and nothing asks the use case. Category only,
 * like the note — no use case carries an entry sequence — and anything but an
 * explicit `objectFirst` is today's date-first flow, so a tenant that
 * configured nothing changes nothing.
 */
export const resolveEntrySequence = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): EntrySequence => {
  // The `_standard` sentinel (until the one-off fill) is not a key the room
  // list `GET /app/categories/:key/rooms` can answer, so it stays date first;
  // the standard category opens object first once its own tile serves it
  // (ADR-0026 §AI).
  if (categoryKey === STANDARD_CATEGORY_KEY) return "dateFirst";
  return categoryConfigFor(config, useCase, categoryKey)?.entrySequence === "objectFirst"
    ? "objectFirst"
    : "dateFirst";
};

/**
 * Does this category ask who the customer is before its first screen (#3069)?
 * For a category where what someone may book depends on who they are — a
 * club's hall, members-only rooms (ADR-0026 §Z). Category only; anything but
 * an explicit `first` keeps today's flow, where guest checkout stays an equal
 * path (ADR-0008 Layer 3).
 *
 * Only on a tenant with customer accounts: without them there is no sign-in
 * to ask for, and asking would lock the flow.
 */
export const resolveSignInFirst = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): boolean =>
  config?.accountsEnabled === true &&
  categoryConfigFor(config, useCase, categoryKey)?.identification === "first";

/**
 * The word the head-count field uses (#3035, M2). Category only; anything but
 * a known word is `guests`, today's label.
 */
export const resolveHeadCountLabel = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): HeadCountLabel => {
  const label = categoryConfigFor(config, useCase, categoryKey)?.headCountLabel;
  return label === "participants" || label === "members" ? label : "guests";
};

/**
 * How the venue step asks for the event — the event-type dropdown (`select`,
 * also when nothing is configured) or a free-text name (`freeText`): "Tischtennis
 * Jugend" rather than a category of occasion, for serial use (PO, 2026-10-03).
 *
 * Category only, like the note: absent means the dropdown, today's behaviour.
 * Anything but the exact `freeText` reads as `select`.
 *
 * The admin-ui answers the same question for its booking screens in
 * `frontend/admin-ui/src/utils/eventTypeInput.ts`: same key and reading, but
 * from the selected room's own category rather than the entry category. A
 * change to the rule changes both.
 */
export const resolveEventTypeInput = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): "select" | "freeText" =>
  categoryConfigFor(config, useCase, categoryKey)?.eventTypeInput === "freeText"
    ? "freeText"
    : "select";

/**
 * The check-in/check-out flow for this booking.
 *
 * Strict on `enabled === true` on the fallback side, matching
 * `resolveUseCaseFlow` in both backends: a truthy-but-not-true value in
 * untrusted JSONB must never open a door. The category side is already
 * filtered that way by the projector before it reaches the wire, and is
 * re-checked here rather than trusted, because this decides physical access.
 */
export const resolveFlowConfig = (
  config: TenantConfig | undefined,
  useCase: TenantConfigService | string,
  categoryKey?: string
): UseCaseFlowConfig | undefined => {
  const fromCategory = categoryConfigFor(config, useCase, categoryKey)?.checkInCheckOut;
  if (fromCategory?.enabled === true) return fromCategory;
  if (fromCategory) return undefined;

  const fromUseCase = config?.checkInCheckOutFlow?.useCases?.[key(useCase)];
  return fromUseCase?.enabled === true ? fromUseCase : undefined;
};

/**
 * The category the customer entered this flow through, or `undefined`.
 *
 * Reads the URL through the same validated parser the search flows use, so
 * "which category is active" has one definition rather than one per screen.
 * Via `useLocation` so it re-resolves on navigation — reading
 * `window.location` directly would go stale the moment the customer moves
 * between steps without a remount.
 */
export const useActiveProductCategory = (): string | undefined => {
  const { search } = useLocation();
  return categoryFromSearch(search);
};
